import React, { useState } from 'react';
import { Input } from 'reactstrap';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { confirmResetPassword } from 'aws-amplify/auth';
import _ from 'lodash';
import StyledReactButton from 'components/Buttons/StyledButton';
import { authActions } from 'redux/auth';
import { store } from 'redux/store';
import ErrorMessage from './ErrorMessage';
import { AuthError } from 'pages/Authenticator/models';

interface PropTypes {
  id: string;
  onClickBack: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const PasswordChange: React.FC<PropTypes> = ({ id, onClickBack }) => {
  const { t } = useTranslation(['forgotPassword', 'formError']);
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [errorInfo, setErrorInfo] = useState<{
    name?: string;
    message?: string;
  }>({});
  const [showInput, setShowInput] = useState(false);
  const [passwordChangeParams, setPasswordChangeParams] = useState({
    code: '',
    password: '',
  });
  const { password, code } = passwordChangeParams;

  const onClickCheckbox = () => {
    setShowInput(showInput => !showInput);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name: key, value } = e.target;
    setValue(key, value);
    setPasswordChangeParams(prevState => ({ ...prevState, [key]: value }));
  };

  const onSubmit = (): void => {
    confirmResetPassword({
      username: id,
      confirmationCode: code,
      newPassword: password,
    })
      .then(() => store.dispatch(authActions.forgetPassword(false)))
      .catch(error => {
        const errorName = error instanceof Error ? error.name : '';
        let errorMessage = error instanceof Error ? error.message : '';
        switch (errorName) {
          case AuthError.codeMismatchException:
            errorMessage = t('codeMismatchException');
            break;
          case AuthError.expiredCodeException:
            errorMessage = t('expiredCodeException');
            break;
          case AuthError.invalidPasswordException:
          case AuthError.invalidParameterException:
            errorMessage = t('passwordIsWeak');
            break;
          case AuthError.limitExceededException:
            errorMessage = t('cognitoLimitExceeded');
            break;
          default:
            errorMessage = t('internalErrorException');
            break;
        }
        setErrorInfo({
          name: errorName,
          message: errorMessage,
        });
      });
  };

  const { message: errorMessage } = errorInfo;
  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <StyledTitle>{t('forgotPassword:submitPasswordTitle')}</StyledTitle>
      <StyledCodeInput>
        <CodeLabel>{t('forgotPassword:code')}</CodeLabel>
        <div>
          <StyledInput
            {...register('code', {
              required: true,
            })}
            value={code}
            onChange={onChange}
          />
        </div>
      </StyledCodeInput>
      <StyledPasswordInput>
        <PasswordLabel>{t('forgotPassword:password')}</PasswordLabel>
        <div>
          <StyledInput
            {...register('password', {
              required: true,
            })}
            type={showInput ? 'text' : 'password'}
            value={password}
            autoComplete={'new-password'}
            onChange={onChange}
          />
        </div>
      </StyledPasswordInput>
      <StyledCheckbox>
        <CheckboxLabel>
          <Input
            type={'checkbox'}
            defaultChecked={showInput}
            onClick={onClickCheckbox}
          />
          {t('forgotPassword:checkPassword')}
        </CheckboxLabel>
      </StyledCheckbox>
      <StyledSubmitButton>
        <StyledReactButton color={'primary'} block>
          {t('forgotPassword:submitNewPassword')}
        </StyledReactButton>
      </StyledSubmitButton>
      {(!_.isEmpty(errors) || errorMessage !== null) && (
        <ErrorMessage errors={errors} errorMessage={errorMessage} />
      )}
      <div>
        <StyledAnchor href={'#'} onClick={onClickBack}>
          {t('forgotPassword:back')}
        </StyledAnchor>
      </div>
    </StyledForm>
  );
};

const StyledForm = styled.form`
  width: 330px;
  text-align: -webkit-center;
`;

const StyledTitle = styled.p`
  margin-bottom: 15px;
  font-size: 2rem;
`;

const StyledInput = styled(Input)`
  width: 200px;
  margin-bottom: 8px;
`;

const StyledSubmitButton = styled.div`
  width: 280px;
  margin-bottom: 20px;
`;

const StyledCodeInput = styled.div`
  display: flex;
  align-items: center;
`;

const CodeLabel = styled.div`
  width: 220px;
  margin-right: 5px;
  font-size: 0.95rem;
  text-align: right;
`;

const StyledPasswordInput = styled.div`
  display: flex;
  align-items: center;
`;

const PasswordLabel = styled.div`
  width: 220px;
  font-size: 0.95rem;
`;

const StyledCheckbox = styled.div`
  margin: 0 10px 20px 0;
  text-align: right;
`;

const CheckboxLabel = styled.label`
  padding-left: 1.25rem;
  > input {
    margin-top: 0.4rem;
  }
`;

const StyledAnchor = styled.a`
  :hover {
    text-decoration: underline;
  }
`;

export default PasswordChange;
